/**
 * Visual-viewport numbers for the pinned mobile shells (portal + staff).
 *
 * Keyboard / iOS chrome keep `scale` at 1 — those height and offsetTop values
 * must reach the CSS vars. Pinch-zoom (or an accessibility zoom) changes
 * `scale` and pans `offsetTop`; applying those would shrink and jump the whole
 * shell. On WebKit `window.innerHeight` follows the *visual* viewport while
 * zoomed, so it is not a safe fallback either. While zoomed we therefore keep
 * the last metrics measured at scale 1 (`previousStable`) and only fall back to
 * the layout height when nothing stable has been measured yet.
 */

export interface VisualViewportShellInput {
  readonly height: number;
  readonly offsetTop: number;
  readonly scale: number;
}

export interface VisualViewportShellMetrics {
  readonly height: number;
  readonly offsetTop: number;
  readonly bottomGap: number;
}

const PINCH_ZOOM_SCALE_EPSILON = 0.01;

/** True while the page is pinch-/accessibility-zoomed (scale ≠ 1). */
export function isVisualViewportZoomed(scale: number): boolean {
  return Math.abs(scale - 1) > PINCH_ZOOM_SCALE_EPSILON;
}

export function visualViewportShellMetrics(
  vv: VisualViewportShellInput,
  layoutHeight: number,
  previousStable: VisualViewportShellMetrics | null = null,
): VisualViewportShellMetrics {
  if (isVisualViewportZoomed(vv.scale)) {
    if (previousStable != null) return previousStable;
    return { height: Math.round(layoutHeight), offsetTop: 0, bottomGap: 0 };
  }
  const height: number = Math.round(vv.height);
  const offsetTop: number = Math.round(vv.offsetTop);
  const bottomGap: number = Math.max(
    0,
    Math.round(layoutHeight - vv.height - vv.offsetTop),
  );
  return { height, offsetTop, bottomGap };
}
